import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { perf } from '../../../lib/perfLogger';

interface QuickFillItem {
  Name: string;
  [key: string]: string | undefined;
}

export type TableSortDirection = 'asc' | 'desc';
export type TableMissingValuePlacement = 'first' | 'last';
export type TableFilterMode = 'values' | 'condition';
export type TableFilterOperator =
  | 'contains'
  | 'notContains'
  | 'equals'
  | 'notEquals'
  | 'startsWith'
  | 'endsWith'
  | 'isEmpty'
  | 'isNotEmpty';

export interface TableSortState {
  column: keyof QuickFillItem | null;
  direction: TableSortDirection;
  missingValuePlacement: TableMissingValuePlacement;
}

export interface TableColumnFilter {
  mode: TableFilterMode;
  operator: TableFilterOperator;
  value: string;
  selectedValues: string[] | null;
}

export interface UseTableFiltersReturn {
  columnFilters: Partial<Record<keyof QuickFillItem, TableColumnFilter>>;
  getUniqueValuesForColumn: (field: keyof QuickFillItem) => string[];
  getValueCountsForColumn: (field: keyof QuickFillItem) => Record<string, number>;
  setColumnValueFilter: (field: keyof QuickFillItem, selectedValues: string[] | null) => void;
  setColumnConditionFilter: (field: keyof QuickFillItem, operator: TableFilterOperator, value: string) => void;
  setColumnSort: (field: keyof QuickFillItem, direction: TableSortDirection) => void;
  clearColumnSort: () => void;
  clearColumnFilter: (field: keyof QuickFillItem) => void;
  resetFilters: () => void;
  hasActiveFilters: boolean;
  activeFilterCount: number;
  filteredData: QuickFillItem[];
  sortState: TableSortState;
}

export interface TableInitialPreset {
  requestId: string;
  exactNameFilters?: string[];
  sort?: {
    column: keyof QuickFillItem;
    direction: TableSortDirection;
    missingValuePlacement?: TableMissingValuePlacement;
  };
}

const normalize = (value: string): string => value.trim().toLowerCase();

const isConditionOperatorValueless = (operator: TableFilterOperator): boolean =>
  operator === 'isEmpty' || operator === 'isNotEmpty';

const isColumnFilterActive = (filter: TableColumnFilter | undefined): boolean => {
  if (!filter) {
    return false;
  }

  if (filter.mode === 'values') {
    return filter.selectedValues !== null;
  }

  return isConditionOperatorValueless(filter.operator) || filter.value.trim().length > 0;
};

const getCellValue = (item: QuickFillItem, field: keyof QuickFillItem): string =>
  item[field]?.toString() ?? '';

interface ColumnValueStats {
  sortedValues: string[];
  counts: Record<string, number>;
}

const matchesCondition = (value: string, operator: TableFilterOperator, filterValue: string): boolean => {
  const normalizedValue = normalize(value);
  const normalizedFilterValue = normalize(filterValue);

  switch (operator) {
    case 'contains':
      return normalizedValue.includes(normalizedFilterValue);
    case 'notContains':
      return !normalizedValue.includes(normalizedFilterValue);
    case 'equals':
      return normalizedValue === normalizedFilterValue;
    case 'notEquals':
      return normalizedValue !== normalizedFilterValue;
    case 'startsWith':
      return normalizedValue.startsWith(normalizedFilterValue);
    case 'endsWith':
      return normalizedValue.endsWith(normalizedFilterValue);
    case 'isEmpty':
      return normalizedValue.length === 0;
    case 'isNotEmpty':
      return normalizedValue.length > 0;
    default:
      return true;
  }
};

const updateColumnFilterState = (
  previous: Partial<Record<keyof QuickFillItem, TableColumnFilter>>,
  field: keyof QuickFillItem,
  filter: TableColumnFilter | null
): Partial<Record<keyof QuickFillItem, TableColumnFilter>> => {
  if (filter === null) {
    if (!previous[field]) {
      return previous;
    }
    const next = { ...previous };
    delete next[field];
    return next;
  }

  return {
    ...previous,
    [field]: filter,
  };
};

export function useTableFilters(
  safeData: QuickFillItem[],
  allColumns: (keyof QuickFillItem)[],
  initialPreset?: TableInitialPreset | null
): UseTableFiltersReturn {
  const collator = useMemo(
    () => new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' }),
    []
  );
  const lastAppliedPresetIdRef = useRef<string | null>(null);
  const [columnFilters, setColumnFilters] = useState<Partial<Record<keyof QuickFillItem, TableColumnFilter>>>({});
  const [sortState, setSortState] = useState<TableSortState>({
    column: null,
    direction: 'asc',
    missingValuePlacement: 'last',
  });

  const uniqueValuesByColumn = useMemo(() => {
    const tUniqueValues = performance.now();
    const valueMap = new Map<keyof QuickFillItem, Map<string, number>>();

    allColumns.forEach((column) => {
      valueMap.set(column, new Map<string, number>());
    });

    safeData.forEach((item) => {
      allColumns.forEach((column) => {
        const value = getCellValue(item, column);
        const counts = valueMap.get(column);
        if (!counts) {
          return;
        }
        counts.set(value, (counts.get(value) ?? 0) + 1);
      });
    });

    // Sort once per data change: the header reads these getters on every render,
    // including every virtualized scroll step, so they must stay O(1) lookups.
    const statsMap = new Map<keyof QuickFillItem, ColumnValueStats>();
    valueMap.forEach((counts, column) => {
      statsMap.set(column, {
        sortedValues: Array.from(counts.keys()).sort(collator.compare),
        counts: Object.fromEntries(counts.entries()),
      });
    });

    perf.slow('QF-TableFilter', 'build unique values by column', tUniqueValues, 50, {
      rows: safeData.length,
      columns: allColumns.length,
    });
    return statsMap;
  }, [allColumns, collator, safeData]);

  const setColumnSort = useCallback((
    field: keyof QuickFillItem,
    direction: TableSortDirection,
    missingValuePlacement: TableMissingValuePlacement = 'last'
  ) => {
    setSortState({ column: field, direction, missingValuePlacement });
  }, []);

  const clearColumnSort = useCallback(() => {
    setSortState({ column: null, direction: 'asc', missingValuePlacement: 'last' });
  }, []);

  const setColumnValueFilter = useCallback((field: keyof QuickFillItem, selectedValues: string[] | null) => {
    perf.log('QF-TableFilter', 'setColumnValueFilter', {
      field,
      selectedValues,
      selectedValueCount: selectedValues?.length ?? null,
    });
    setColumnFilters((prev) => {
      return updateColumnFilterState(
        prev,
        field,
        selectedValues === null
          ? null
          : {
          mode: 'values',
          operator: 'contains',
          value: '',
          selectedValues,
        }
      );
    });
  }, []);

  const setColumnConditionFilter = useCallback((
    field: keyof QuickFillItem,
    operator: TableFilterOperator,
    value: string
  ) => {
    perf.log('QF-TableFilter', 'setColumnConditionFilter', {
      field,
      operator,
      value,
    });
    setColumnFilters((prev) => {
      const trimmedValue = value.trim();
      return updateColumnFilterState(
        prev,
        field,
        !isConditionOperatorValueless(operator) && trimmedValue.length === 0
          ? null
          : {
          mode: 'condition',
          operator,
          value,
          selectedValues: null,
        }
      );
    });
  }, []);

  const clearColumnFilter = useCallback((field: keyof QuickFillItem) => {
    perf.log('QF-TableFilter', 'clearColumnFilter', { field });
    setColumnFilters((prev) => updateColumnFilterState(prev, field, null));
  }, []);

  const resetFilters = useCallback(() => {
    perf.log('QF-TableFilter', 'resetFilters');
    setColumnFilters({});
  }, []);

  useEffect(() => {
    if (!initialPreset?.requestId || lastAppliedPresetIdRef.current === initialPreset.requestId) {
      return;
    }

    lastAppliedPresetIdRef.current = initialPreset.requestId;

    const exactNameFilters = Array.from(
      new Set((initialPreset.exactNameFilters ?? []).map((value) => value.trim()).filter(Boolean))
    );

    setColumnFilters(
      exactNameFilters.length > 0
        ? {
            Name: {
              mode: 'values',
              operator: 'contains',
              value: '',
              selectedValues: exactNameFilters,
            },
          }
        : {}
    );

    setSortState(
      initialPreset.sort
        ? {
            column: initialPreset.sort.column,
            direction: initialPreset.sort.direction,
            missingValuePlacement: initialPreset.sort.missingValuePlacement ?? 'last',
          }
        : {
            column: null,
            direction: 'asc',
            missingValuePlacement: 'last',
          }
    );
  }, [initialPreset]);

  const activeFilterEntries = useMemo(
    () =>
      Object.entries(columnFilters).filter(([field, filter]) =>
        isColumnFilterActive(filter as TableColumnFilter | undefined) && allColumns.includes(field as keyof QuickFillItem)
      ) as Array<[keyof QuickFillItem, TableColumnFilter]>,
    [allColumns, columnFilters]
  );

  const hasActiveFilters = activeFilterEntries.length > 0;
  const activeFilterCount = activeFilterEntries.length;

  const filteredData = useMemo(() => {
    const tFilter = performance.now();
    const endFilter = perf.start('Table', 'filter+sort');

    const filtered = activeFilterEntries.length === 0
      ? safeData
      : safeData.filter((item) => {
          for (const [field, filter] of activeFilterEntries) {
            const cellValue = getCellValue(item, field);

            if (filter.mode === 'values') {
              if (filter.selectedValues === null) {
                continue;
              }
              if (!filter.selectedValues.includes(cellValue)) {
                return false;
              }
              continue;
            }

            if (!matchesCondition(cellValue, filter.operator, filter.value)) {
              return false;
            }
          }

          return true;
        });

    if (!sortState.column) {
      endFilter();
      perf.slow('QF-TableFilter', 'filter rows', tFilter, 50, {
        totalRows: safeData.length,
        filteredRows: filtered.length,
        activeFilters: activeFilterEntries.length,
        sortColumn: null,
      });
      return filtered;
    }

    const sorted = filtered
      .map((item, index) => ({ item, index }))
      .sort((left, right) => {
        const leftValue = getCellValue(left.item, sortState.column!).trim();
        const rightValue = getCellValue(right.item, sortState.column!).trim();
        const leftMissing = leftValue.length === 0;
        const rightMissing = rightValue.length === 0;

        if (leftMissing && rightMissing) {
          return left.index - right.index;
        }
        if (leftMissing) {
          return sortState.missingValuePlacement === 'first' ? -1 : 1;
        }
        if (rightMissing) {
          return sortState.missingValuePlacement === 'first' ? 1 : -1;
        }

        const compared = collator.compare(leftValue, rightValue);
        if (compared !== 0) {
          return sortState.direction === 'asc' ? compared : -compared;
        }

        return left.index - right.index;
      })
      .map(({ item }) => item);

    endFilter();
    perf.slow('QF-TableFilter', 'filter and sort rows', tFilter, 50, {
      totalRows: safeData.length,
      filteredRows: sorted.length,
      activeFilters: activeFilterEntries.length,
      sortColumn: sortState.column,
      sortDirection: sortState.direction,
    });
    return sorted;
  }, [activeFilterEntries, collator, safeData, sortState]);

  useEffect(() => {
    perf.log('QF-TableFilter', 'filter state snapshot', {
      activeFilters: activeFilterEntries.map(([field, filter]) => ({
        field,
        mode: filter.mode,
        operator: filter.operator,
        value: filter.value,
        selectedValues: filter.selectedValues,
      })),
      filteredRows: filteredData.length,
      totalRows: safeData.length,
      sortColumn: sortState.column,
      sortDirection: sortState.direction,
      sampleRows: filteredData.slice(0, 3).map((item) => item.Name),
    });
  }, [activeFilterEntries, filteredData, safeData.length, sortState.column, sortState.direction]);

  const getUniqueValuesForColumn = useCallback((field: keyof QuickFillItem): string[] => {
    return uniqueValuesByColumn.get(field)?.sortedValues ?? [];
  }, [uniqueValuesByColumn]);

  const getValueCountsForColumn = useCallback((field: keyof QuickFillItem): Record<string, number> => {
    return uniqueValuesByColumn.get(field)?.counts ?? {};
  }, [uniqueValuesByColumn]);

  return {
    columnFilters,
    getUniqueValuesForColumn,
    getValueCountsForColumn,
    setColumnValueFilter,
    setColumnConditionFilter,
    setColumnSort,
    clearColumnSort,
    clearColumnFilter,
    resetFilters,
    hasActiveFilters,
    activeFilterCount,
    filteredData,
    sortState,
  };
}
